import type { ReviewDto } from '@rp/contracts';
import { Badge, Stars } from './ui';
import { PLATFORM_LABEL, ratingColor, relativeDate, replyAttribution, STATUS_LABEL } from '../lib/format';
import { haptic } from '../lib/telegram';

const STATUS_COLOR: Record<string, string> = {
  NEW: 'var(--color-neutral)',
  NEEDS_REPLY: 'var(--color-negative)',
  DRAFT_READY: 'var(--color-link)',
  ANSWERED: 'var(--color-positive)',
  SKIPPED: 'var(--color-hint)',
};

/**
 * Карточка отзыва в ленте.
 *
 * Слева — цветная полоса по оценке: она даёт понять тон отзыва раньше, чем
 * прочитан текст, и делает список сканируемым без чтения каждой карточки.
 */
export function ReviewCard({ review, onOpen }: { review: ReviewDto; onOpen: (id: string) => void }) {
  const published = review.reply?.status === 'PUBLISHED';
  const needsAttention = review.status === 'NEW' || review.status === 'NEEDS_REPLY';

  return (
    <button
      onClick={() => {
        haptic.tap();
        onOpen(review.id);
      }}
      className="pressable-row relative mb-2 block w-full overflow-hidden rounded-[var(--radius-card)] bg-[var(--color-section)] px-4 py-3.5 text-left"
    >
      <span
        aria-hidden="true"
        className="absolute inset-y-0 left-0 w-[3px]"
        style={{ background: ratingColor(review.rating) }}
      />

      <header className="mb-1.5 flex items-center gap-2">
        <Stars rating={review.rating} />
        <span className="t-footnote truncate text-[var(--color-hint)]">
          {PLATFORM_LABEL[review.platform]} · {relativeDate(review.publishedAt)}
        </span>
        <span className="ml-auto">
          <Badge color={STATUS_COLOR[review.status]}>{STATUS_LABEL[review.status]}</Badge>
        </span>
      </header>

      <p className="t-subhead font-semibold">{review.authorName}</p>
      <p className="t-callout mt-1 line-clamp-4 whitespace-pre-line">
        {review.text || <span className="text-[var(--color-hint)]">Оценка без текста</span>}
      </p>

      {review.topics.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1.5">
          {review.topics.map((topic) => (
            <Badge key={topic}>{topic}</Badge>
          ))}
        </div>
      )}

      {review.reply ? (
        <div
          className="mt-3 rounded-[10px] border-l-2 bg-[var(--color-surface)] px-3 py-2.5"
          style={{ borderColor: published ? 'var(--color-positive)' : 'var(--color-link)' }}
        >
          <p className="t-caption mb-1 font-medium text-[var(--color-hint)]">
            {published ? 'Ответ' : 'Черновик'} · {replyAttribution(review.reply)}
          </p>
          <p className="t-footnote line-clamp-3">{review.reply.text}</p>
          {review.reply.status === 'FAILED' && review.reply.error && (
            <p className="t-caption mt-1.5 text-[var(--color-danger)]">
              Не опубликован: {review.reply.error}
            </p>
          )}
        </div>
      ) : (
        needsAttention && (
          <p className="t-footnote mt-2.5 text-[var(--color-hint)]">Ответа пока нет — откройте, чтобы написать</p>
        )
      )}
    </button>
  );
}
